import {RendererState} from '@cascade/core';
import {useEffect, useState} from 'react';
import {useApplication} from '../../contexts';
import {useDuration, useRendererState, useSubscribableValue} from '../../hooks';
import {formatDuration} from '../../utils';
import {
  PlaybackControls,
  PlaybackProgress,
  RenderingProgress,
} from '../playback';
import {CurrentTime} from '../playback/CurrentTime';
import {EditorPreview} from './EditorPreview';
import {Timestamp} from './Timestamp';
import styles from './Viewport.module.scss';

export function Viewport() {
  const state = useRendererState();
  return state === RendererState.Working ? (
    <RenderingViewport />
  ) : (
    <EditorViewport />
  );
}

function EditorViewport() {
  const duration = useDuration();

  return (
    <div className={styles.root}>
      <EditorPreview />
      <PlaybackProgress />
      <div className={styles.playback}>
        <CurrentTime
          render={time => (
            <Timestamp
              className={styles.time}
              title="Current time"
              frame={time}
            />
          )}
        />
        <PlaybackControls />
        <Timestamp
          className={styles.duration}
          title="Duration"
          frame={duration}
        />
      </div>
    </div>
  );
}

function RenderingViewport() {
  const {renderer} = useApplication();
  const [estimate, setEstimate] = useState({elapsed: 0, eta: 0, completion: 0});

  useEffect(() => {
    if (!renderer.estimator) return;
    const id = setInterval(() => {
      setEstimate(renderer.estimator.estimate());
    }, 100);
    return () => clearInterval(id);
  }, []);

  const completion = useSubscribableValue(
    renderer.estimator?.onCompletionChanged,
  );

  return (
    <div className={styles.root}>
      <div
        className={styles.viewport}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.5)',
          fontSize: 14,
        }}
      >
        Rendering via Remotion... {Math.round(((completion as number) ?? 0) * 100)}%
      </div>
      <RenderingProgress />
      <div className={styles.playback}>
        <code
          className={styles.time}
          title="Time elapsed since the rendering started"
        >
          {formatDuration(estimate.elapsed / 1000)}
          <span className={styles.frames}>Elapsed</span>
        </code>
        <div />
        <code
          className={styles.duration}
          title="Estimated time remaining until the rendering is complete"
        >
          <span className={styles.frames}>ETA:</span>
          {formatDuration(estimate.eta / 1000)}
        </code>
      </div>
    </div>
  );
}
